import { useStorage } from "@vueuse/core";
import { type CalendarInsightData, CalendarInsightSchema, type CalendarIntegrationViewEvent } from "~vue/types/calendar_integration";
import { type ConnectionCalendarAttendee } from "~vue/types/connection";
import { type Reactive, reactive, type Ref } from "vue";
import { z } from "zod";

export const CachedCalendarInsightSchema = z.object({
    insight: CalendarInsightSchema,
    cachedAt: z.number(),
});
export type CachedCalendarInsight = z.infer<typeof CachedCalendarInsightSchema>;

const cachedCalendarInsightSerializer = {
    read(raw: string): CachedCalendarInsight | null {
        if (raw === null || raw === undefined || raw === "") {
            return null;
        }
        try {
            const parsed = CachedCalendarInsightSchema.safeParse(JSON.parse(raw));
            return parsed.success ? parsed.data : null;
        } catch {
            return null;
        }
    },
    write(value: CachedCalendarInsight | null): string {
        return JSON.stringify(value);
    },
};

export const cachedCalendarInsightRef: Ref<CachedCalendarInsight | null> = useStorage<CachedCalendarInsight | null>("cached_calendar_insight", null, undefined, {
    serializer: cachedCalendarInsightSerializer,
});

export interface CalendarStore {
    showConnectCalendarDialog: boolean;
    setShowConnectCalendarDialog: (value: boolean) => void;
    dialogAttendees: ConnectionCalendarAttendee[];
    setDialogAttendees: (attendees: ConnectionCalendarAttendee[]) => void;
    showAttendeesDialog: boolean;
    setShowAttendeesDialog: (value: boolean) => void;
    activeEvent: CalendarIntegrationViewEvent | null;
    setActiveEvent: (event: CalendarIntegrationViewEvent | null) => void;
    activeElement: HTMLDivElement | null;
    setActiveElement: (element: HTMLDivElement | null) => void;
    cachedCalendarInsight: CachedCalendarInsight | null;
    setCachedCalendarInsight: (insight: CalendarInsightData | null) => void;
}

// State store for homepage components
export const useCalendarStore: Reactive<CalendarStore> = reactive<CalendarStore>({
    showConnectCalendarDialog: false as boolean,
    setShowConnectCalendarDialog(value: boolean) {
        this.showConnectCalendarDialog = value;
    },
    dialogAttendees: [] as ConnectionCalendarAttendee[],
    setDialogAttendees(attendees: ConnectionCalendarAttendee[]) {
        this.dialogAttendees = attendees;
    },
    showAttendeesDialog: false as boolean,
    setShowAttendeesDialog(value: boolean) {
        this.showAttendeesDialog = value;
    },
    activeEvent: null as CalendarIntegrationViewEvent | null,
    setActiveEvent(event: CalendarIntegrationViewEvent | null) {
        this.activeEvent = event;
    },
    activeElement: null as HTMLDivElement | null, // template ref
    setActiveElement(element: HTMLDivElement | null) {
        this.activeElement = element;
    },
    get cachedCalendarInsight(): CachedCalendarInsight | null {
        return cachedCalendarInsightRef.value;
    },
    setCachedCalendarInsight(insight: CalendarInsightData | null) {
        cachedCalendarInsightRef.value = insight ? { insight, cachedAt: Date.now() } : null;
    },
});
